import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Eye, EyeSlash, CaretRight, LockKey, CreditCard } from "@phosphor-icons/react";
import GcrLogo from "../components/GcrLogo";
import apiClient from "../lib/api";

const PLANS = [
  { id: "weekly",  label: "Semanal",  desc: "7 días de acceso completo",  price: 50 },
  { id: "monthly", label: "Mensual",  desc: "30 días — más popular",       price: 160 },
];

function SuspendedReactivate({ email }) {
  const [planId, setPlanId] = useState("monthly");
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.post("/payments/reactivation-checkout", {
        email,
        plan_id: planId,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear el pago");
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: "rgba(239,68,68,0.05)",
      border: "1px solid rgba(239,68,68,0.2)",
      borderRadius: 16,
      padding: 24,
      marginTop: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <LockKey weight="fill" style={{ color: "#EF4444", width: 20, height: 20 }} />
        <span style={{ color: "#EF4444", fontWeight: 800, fontSize: 14, textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Cuenta suspendida
        </span>
      </div>
      <p style={{ color: "#A1A1AA", fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
        Reactiva tu acceso completando el pago de tu suscripción.
      </p>

      {/* Plan selector */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        {PLANS.map(p => (
          <button key={p.id} onClick={() => setPlanId(p.id)} style={{
            flex: 1, padding: "12px 8px", borderRadius: 10, textAlign: "center", cursor: "pointer",
            background: planId === p.id ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.03)",
            border: planId === p.id ? "1.5px solid #D4AF37" : "1px solid rgba(255,255,255,0.08)",
            transition: "all 0.2s",
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: planId === p.id ? "#D4AF37" : "#71717A" }}>
              {p.label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: planId === p.id ? "#fff" : "#A1A1AA", margin: "4px 0" }}>
              ${p.price}
            </div>
            <div style={{ fontSize: 10, color: "#52525B" }}>{p.desc}</div>
          </button>
        ))}
      </div>

      <button
        onClick={handlePay}
        disabled={loading}
        data-testid="reactivate-pay-btn"
        style={{
          width: "100%", padding: "14px", borderRadius: 10, border: "none",
          background: loading ? "#52525B" : "#D4AF37", color: "#050508",
          fontWeight: 900, fontSize: 13, textTransform: "uppercase",
          letterSpacing: "0.15em", cursor: loading ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          transition: "all 0.2s",
        }}
      >
        <CreditCard weight="fill" style={{ width: 16, height: 16 }} />
        {loading ? "Redirigiendo a pago..." : "Pagar y Reactivar acceso"}
      </button>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suspended, setSuspended] = useState(false);

  const from = loc.state?.from?.pathname;

  // Show success message if coming back from reactivation payment
  const reactivated = new URLSearchParams(loc.search).get("reactivated");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuspended(false);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      let dest;
      if (user.role === "admin") {
        dest = (from && from.startsWith("/admin")) ? from : "/admin";
      } else {
        dest = from || "/jugadas";
      }
      toast.success(`Bienvenido, ${user.name}`);
      nav(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "account_suspended") {
        setSuspended(true);
      } else {
        toast.error(detail || "Email o contraseña incorrectos");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 text-white" style={{ background: "#050508" }}>
      {/* Left side - form */}
      <div className="flex flex-col justify-center px-8 lg:px-16 py-10 relative" style={{ background: "#050508" }}>
        <Link to="/" className="absolute top-8 left-8" data-testid="login-back-home">
          <GcrLogo size={56} />
        </Link>

        <div className="max-w-sm w-full mx-auto">
          <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-3" style={{ color: "#D4AF37" }}>
            Bienvenido de vuelta
          </div>
          <h1 className="font-sport font-black leading-[1.0] mb-4" style={{ fontSize: "clamp(2.4rem, 6vw, 3.5rem)", letterSpacing: "-0.03em" }}>
            Inicia<br />sesión
          </h1>
          <p className="text-sm mb-8" style={{ color: "#52525B" }}>
            Accede a tus jugadas del día y análisis completo.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase font-black mb-2" style={{ color: "#9C9CAA" }}>Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="input-field"
                placeholder="tu@email.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[10px] tracking-[0.2em] uppercase font-black" style={{ color: "#9C9CAA" }}>Contraseña</label>
                <Link to="/forgot" className="text-[10px] uppercase tracking-wider transition-colors" style={{ color: "#52525B" }}
                  onMouseEnter={e => e.target.style.color="#D4AF37"} onMouseLeave={e => e.target.style.color="#52525B"}>
                  ¿Olvidaste?
                </Link>
              </div>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="input-field pr-10"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#71717A" }} data-testid="login-toggle-password">
                  {show ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full mt-6 py-4 text-sm tracking-widest uppercase font-black" data-testid="login-submit">
              {loading ? "Accediendo..." : <>Entrar <CaretRight weight="bold" className="w-4 h-4 inline" /></>}
            </button>
          </form>

          {suspended && <SuspendedReactivate email={email} />}

          {reactivated && (
            <div style={{
              background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)",
              borderRadius: 10, padding: "12px 16px", marginTop: 16,
              color: "#10B981", fontSize: 13, fontWeight: 700, textAlign: "center",
            }}>
              ✓ Pago completado. Inicia sesión para acceder.
            </div>
          )}

          <div className="mt-6 text-xs text-center" style={{ color: "#71717A" }}>
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="font-semibold transition-colors" style={{ color: "#D4AF37" }}
              data-testid="login-go-register">
              Regístrate gratis
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - visual */}
      <div className="hidden lg:block relative overflow-hidden" style={{ borderLeft: "1px solid #0E0E14" }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/15779126/pexels-photo-15779126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920)', opacity: 0.35 }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #050508/90 0%, #050508/60 50%, #050508 100%)" }} />
        {/* Gold grain overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(ellipse at 30% 50%, rgba(212,175,55,0.08) 0%, transparent 70%)",
        }} />
        <div className="relative h-full flex items-end justify-start p-16">
          <div className="max-w-md">
            <div style={{ height: 1, background: "linear-gradient(90deg, #D4AF37, transparent)", marginBottom: 24, width: 48 }} />
            <blockquote className="font-sport font-black leading-tight mb-6" style={{ fontSize: "clamp(1.4rem, 3vw, 2rem)", letterSpacing: "-0.02em" }}>
              "Cada jugada explicada en{" "}
              <span className="text-gradient-gold">palabras simples</span>,{" "}
              con números que la respaldan."
            </blockquote>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.3)" }}>
                <span className="font-black text-sm" style={{ color: "#D4AF37" }}>G</span>
              </div>
              <div>
                <div className="text-sm font-semibold text-white">La promesa de la casa</div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "#52525B" }}>Señales claras, sin jerga</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
